import type { AsyncScore } from '@fs/zion-person-quality-score-issues'
import { useFeatureFlag } from '@fs/zion-flags'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { useSwrRequest } from './useSwrRequest'
import useFocusPerson from './useFocusPerson'
import useQualityIndicatorFlag from './useQualityIndicatorFlag'

const swrKey = (personId: string | undefined, treeId: string, limitScope: boolean): null | string =>
  personId
    ? `/service/tree/tree-data/quality/person/${personId}/scores?treeId=${treeId}${!limitScope ? '&limitScope=false' : ''}`
    : null

export default function usePersonQualityScore(): {
  score: AsyncScore | undefined
  loading: boolean
  showPersonQualityScore: boolean
  mutatePersonQualityScore: ReturnType<typeof useSwrRequest<AsyncScore | ''>>['mutate']
} {
  const { focusPerson } = useFocusPerson()
  const { currentTreeId } = useGroupsContext()
  const limitScope = !useFeatureFlag('treeWeb_developerMode').isOn

  const { data: qualityIndicatorFlag } = useQualityIndicatorFlag()
  const showPersonQualityScore = !!qualityIndicatorFlag?.isOn

  const {
    data: score,
    isLoading,
    mutate: mutatePersonQualityScore,
  } = useSwrRequest<AsyncScore | ''>(
    swrKey(showPersonQualityScore ? focusPerson?.id : undefined, currentTreeId || 'PRIVATE', limitScope),
    undefined,
    {
      refreshInterval: (latestData) => {
        if (typeof latestData === 'object' && !latestData.isValid) {
          if (latestData.currentCalculationDurationMillis < 1_000) {
            return 200
          }
          if (latestData.currentCalculationDurationMillis < 5_000) {
            return 1_000
          }
          if (latestData.currentCalculationDurationMillis < 10_000) {
            return 2_000
          }
        }
        return 0
      },
    }
  )

  if (typeof score === 'object' && !score.isValid && score.currentCalculationDurationMillis >= 10_000) {
    //  Show "Not Available At This Time"
    score.visibility = 'FORBIDDEN'
  }

  return {
    loading: isLoading,
    showPersonQualityScore,
    score: score === '' ? undefined : score,
    mutatePersonQualityScore,
  }
}
